/**
 * Centrální zdroj navigačních sekcí.
 * Cesty jsou absolutní pod /app/* — veřejná landing leží mimo tento strom.
 */

import type { IconName } from '../icons/Icon';

export interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** Klíč pro dynamický odznak (počítaný v Sidebaru). */
  badgeKey?: string;
}

export interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
  /** Tlačítko ve spodní liště na mobilu (krátký popisek + ikona). Bez něj jen v „Více". */
  mobile?: { label: string; icon: IconName };
}

export const navSections: NavSection[] = [
  {
    id: 'prehled',
    label: 'Přehled',
    items: [
      { to: '/app', label: 'Rozcestník', icon: '◉' },
      { to: '/app/stavby', label: 'Moje stavby', icon: '◱' },
      { to: '/app/ukoly', label: 'Úkoly', icon: '✓' },
    ],
  },
  {
    id: 'dokumenty',
    mobile: { label: 'Dokumenty', icon: 'letterhead' },
    label: 'Stavební dokumenty',
    items: [
      { to: '/app/predavaci', label: 'Předávací protokol — staveniště', icon: '✎' },
      { to: '/app/predavaci-dilo', label: 'Předávací protokol — dílo', icon: '✎' },
      { to: '/app/predavaci-dilo-velky', label: 'Předávací protokol — dílo (velký)', icon: '✎' },
      { to: '/app/soupis', label: 'Soupis prací', icon: '≡' },
      { to: '/app/zjistovaci', label: 'Zjišťovací protokol', icon: '▣' },
      { to: '/app/faktura', label: 'Faktura', icon: '₡' },
      { to: '/app/nabidka', label: 'Cenová nabídka', icon: '₡' },
      { to: '/app/objednavka', label: 'Objednávka', icon: '⎙' },
      { to: '/app/reklamace', label: 'Reklamační protokol', icon: '!' },
      { to: '/app/podepsat', label: 'Podepsat dokument (PDF/JPG)', icon: '✍' },
    ],
  },
  {
    id: 'realizace',
    mobile: { label: 'Realizace', icon: 'crane' },
    label: 'Realizace',
    items: [
      { to: '/app/denik', label: 'Stavební deník', icon: '▢' },
      { to: '/app/kontrolni-dny', label: 'Kontrolní dny', icon: '◱' },
      { to: '/app/kontrolni-dny/nasdilene', label: 'Nasdílené KD', icon: '📥' },
      { to: '/app/dokumenty-projektu', label: 'Projektová dokumentace', icon: '≡' },
      { to: '/app/fotodokumentace', label: 'Fotodokumentace', icon: '◉' },
      { to: '/app/vady', label: 'Vady', icon: '!' },
      { to: '/app/rfi', label: 'Dotazy k projektu', icon: '?' },
      { to: '/app/harmonogram', label: 'Harmonogram', icon: '≡' },
      { to: '/app/zmeny', label: 'Změnové listy', icon: '✎' },
    ],
  },
  {
    id: 'bozp',
    mobile: { label: 'BOZP', icon: 'medical' },
    label: 'BOZP',
    items: [
      { to: '/app/bozp/telefony', label: 'Tel. čísla', icon: '☎' },
      { to: '/app/bozp/trauma', label: 'Trauma plán', icon: '✚' },
      { to: '/app/bozp/seznameni', label: 'Seznámení BOZP', icon: '✓' },
      { to: '/app/bozp/urazy', label: 'Kniha úrazů', icon: '!' },
      { to: '/app/bozp/rizika', label: 'Registr rizik', icon: '△' },
      { to: '/app/bozp/pozarni-dohled', label: 'Požární dohled', icon: '🔥' },
      { to: '/app/bozp/karty-zamestnancu', label: 'Karty zaměstnanců', icon: '🦺', badgeKey: 'obnova' },
    ],
  },
  {
    id: 'obchod',
    mobile: { label: 'Obchod', icon: 'wallet' },
    label: 'Obchod',
    items: [
      { to: '/app/subdodavatele', label: 'Kontakty', icon: '▣' },
      { to: '/app/kosilka', label: 'Košilka SoD/objednávky', icon: '🗂', badgeKey: 'kosilky' },
      { to: '/app/vyuctovani', label: 'Vyúčtování', icon: '▦', badgeKey: 'prevody' },
      { to: '/app/kniha-jizd', label: 'Kniha jízd', icon: '⛟' },
      { to: '/app/archiv', label: 'Archiv dokumentů', icon: '⎙' },
    ],
  },
];

export const mobileNavSections = navSections.filter((s) => s.mobile);

/** Sekce, do které patří aktuální cesta (nejdelší shoda; rozcestník /app se nepočítá). */
export function sectionForPath(pathname: string): string | null {
  let best: { id: string; len: number } | null = null;
  for (const s of navSections) {
    for (const i of s.items) {
      if (i.to === '/app') continue;
      if (pathname === i.to || pathname.startsWith(i.to + '/')) {
        if (!best || i.to.length > best.len) best = { id: s.id, len: i.to.length };
      }
    }
  }
  return best?.id ?? null;
}
